
import { useEffect, useState } from 'react';
import { ClipboardCheck } from 'lucide-react';
import { PainelStatCard } from '@/components/painel/PainelStatCard';
import { PainelActivityList, type PainelActivityItem } from '@/components/painel/PainelActivityList';
import { useCms } from '@/components/painel/cms/cms-context';
import { usePowersCms } from '@/components/painel/powers/powers-cms-context';
import { useAcesCms } from '@/components/painel/aces/aces-cms-context';
import { PainelAnalyticsPreview } from '@/components/painel/dashboard/PainelAnalyticsPreview';
import { PainelRecentDrafts } from '@/components/painel/dashboard/PainelRecentDrafts';
import {
  PainelRoleQuickActions,
  PainelRoleWelcomeHeader,
  ROLE_STAT_KEYS,
  STAT_META,
} from '@/components/painel/dashboard/painel-role-dashboard';
import { usePainelSession } from '@/components/painel/rbac/painel-session-context';
import { useAnalyticsOverview } from '@/hooks/use-analytics-overview';
import { useAnalyticsGuides } from '@/hooks/use-analytics-guides';
import { getPainelUsers } from '@/lib/painel-users-store';
import type { PainelRole } from '@/lib/painel-rbac';
import { resolveArticleActor } from '@/lib/painel-activity';
import type { CmsArticle } from '@/lib/cms-types';

function formatRelative(iso: string) {
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Agora mesmo';
    if (mins < 60) return `Há ${mins} min`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `Há ${hrs} h`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `Há ${days} ${days === 1 ? 'dia' : 'dias'}`;
    return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'medium', timeStyle: 'short' }).format(d);
  } catch {
    return iso;
  }
}

function formatAbsolute(iso: string) {
  try {
    return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso)
    );
  } catch {
    return iso;
  }
}

function formatStatNumber(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '0';
  if (n >= 10_000) {
    return new Intl.NumberFormat('pt-BR', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(n);
  }
  return new Intl.NumberFormat('pt-BR').format(n);
}

function toActivityItem(
  article: CmsArticle,
  id: string,
  kind: PainelActivityItem['kind'],
  action: string
): PainelActivityItem {
  const actor = resolveArticleActor(article);
  return {
    id,
    kind,
    action,
    title: article.title,
    relativeTime: formatRelative(article.updatedAt),
    absoluteTime: formatAbsolute(article.updatedAt),
    actorName: actor.name,
    actorInitials: actor.initials,
  };
}

function buildEditorActivity(articles: ReturnType<typeof useCms>['articles']): PainelActivityItem[] {
  return [...articles]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 6)
    .map((a) =>
      toActivityItem(
        a,
        a.id,
        a.status === 'published' ? 'guide-published' : 'draft',
        a.status === 'published' ? 'Guia publicado' : 'Rascunho atualizado'
      )
    );
}

function buildReviewerActivity(articles: ReturnType<typeof useCms>['articles']): PainelActivityItem[] {
  const pending = articles
    .filter((a) => a.status === 'pending_review' || a.status === 'draft')
    .slice(0, 4)
    .map((a) =>
      toActivityItem(
        a,
        `rev-${a.id}`,
        'draft',
        a.status === 'pending_review' ? 'Aguardando revisão' : 'Rascunho'
      )
    );

  const recent = articles
    .filter((a) => a.status === 'published')
    .slice(0, 3)
    .map((a) => toActivityItem(a, `pub-${a.id}`, 'guide-published', 'Publicado recentemente'));

  return [...pending, ...recent].slice(0, 6);
}

type DashboardCounts = {
  guides: number;
  powers: number;
  aces: number;
  users: number;
  views: number;
  categories: number;
  drafts: number;
  review: number;
  published: number;
};

function getStatValue(
  key: (typeof ROLE_STAT_KEYS)[PainelRole][number],
  ready: boolean,
  counts: DashboardCounts
): string {
  if (!ready) return '—';
  return formatStatNumber(counts[key] ?? 0);
}

function RoleDashboardBody({ role }: { role: PainelRole }) {
  const { session } = usePainelSession();
  const { articles, categories, hydrated: cmsHydrated } = useCms();
  const { powers, hydrated: powersHydrated } = usePowersCms();
  const { aces, hydrated: acesHydrated } = useAcesCms();
  const { data: overview, loading: overviewLoading } = useAnalyticsOverview();
  const { data: guidesAnalytics, loading: guidesLoading } = useAnalyticsGuides();
  const [userCount, setUserCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getPainelUsers()
      .then((users) => {
        if (!cancelled) setUserCount(users.length);
      })
      .catch(() => {
        if (!cancelled) setUserCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const draftCount = articles.filter((a) => a.status === 'draft').length;
  const reviewCount = articles.filter((a) => a.status === 'pending_review').length;
  const publishedCount = articles.filter((a) => a.status === 'published').length;
  const views =
    overview.pageviews > 0
      ? overview.pageviews
      : guidesAnalytics.totalViews > 0
        ? guidesAnalytics.totalViews
        : 0;

  const counts: DashboardCounts = {
    guides: articles.length,
    powers: powers.length,
    aces: aces.length,
    users: userCount ?? 0,
    views,
    categories: categories.length,
    drafts: draftCount + reviewCount,
    review: reviewCount,
    published: publishedCount,
  };

  const ready =
    cmsHydrated &&
    powersHydrated &&
    acesHydrated &&
    userCount !== null &&
    !overviewLoading &&
    !guidesLoading;

  const statKeys = ROLE_STAT_KEYS[role];

  const showAnalytics = role === 'super_admin' || role === 'admin';
  const showRecentDrafts = role === 'editor' || role === 'super_admin' || role === 'admin';
  const activityItems =
    role === 'reviewer'
      ? buildReviewerActivity(articles)
      : buildEditorActivity(articles);

  const activityListItems =
    activityItems.length > 0
      ? activityItems
      : [
          {
            id: 'empty',
            kind: 'hint' as const,
            action: role === 'reviewer' ? 'Fila vazia' : 'Primeiros passos',
            title:
              role === 'reviewer'
                ? 'Nenhum conteúdo aguardando revisão no momento.'
                : 'Crie guias, powers ou páginas pelo menu lateral.',
            relativeTime: 'Painel local',
            actorName: session?.name ?? 'Equipe',
            actorInitials: session?.name?.slice(0, 2).toUpperCase() ?? 'XA',
          },
        ];

  return (
    <>
      <section className="space-y-4">
        <div className="h-px w-full bg-gradient-to-r from-transparent via-sky-400/25 to-transparent" />
        <div>
          <h2 className="font-poppins text-lg font-bold text-white">Resumo</h2>
          <p className="mt-0.5 font-inter text-xs text-slate-400">
            {role === 'reviewer'
              ? 'Fila de revisão e publicações recentes'
              : role === 'editor'
                ? 'Seu trabalho recente no portal'
                : 'Números principais do Help Center'}
          </p>
        </div>
        <div
          className={`grid gap-4 sm:grid-cols-2 ${statKeys.length >= 4 ? 'xl:grid-cols-4' : 'xl:grid-cols-3'}`}
        >
          {statKeys.map((key) => {
            const meta = STAT_META[key];
            const Icon = meta.icon;
            return (
              <PainelStatCard
                key={key}
                title={meta.title}
                value={getStatValue(key, ready, counts)}
                icon={Icon}
                description={
                  key === 'review'
                    ? 'Conteúdos na fila'
                    : key === 'drafts'
                      ? 'Aguardando publicação'
                      : key === 'views'
                        ? 'Pageviews / guias'
                        : undefined
                }
                variant={meta.variant}
              />
            );
          })}
        </div>
      </section>

      <PainelRoleQuickActions role={role} />

      <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      <div className={`grid gap-6 ${showAnalytics ? 'xl:grid-cols-12' : ''}`}>
        <div className={`space-y-6 ${showAnalytics ? 'xl:col-span-7' : ''}`}>
          {showRecentDrafts ? (
            <div className="grid gap-6 lg:grid-cols-2 lg:items-stretch">
              <PainelActivityList className="min-h-0" items={activityListItems} />
              <PainelRecentDrafts className="min-h-0" articles={articles} />
            </div>
          ) : (
            <PainelActivityList items={activityListItems} />
          )}
          {role === 'reviewer' && (
            <section className="rounded-2xl border border-white/10 bg-blue-950/45 p-5 backdrop-blur-md">
              <div className="flex items-center gap-2 text-sky-300">
                <ClipboardCheck className="h-5 w-5" />
                <h3 className="font-poppins text-sm font-semibold text-white">Dica de revisão</h3>
              </div>
              <p className="mt-2 font-inter text-sm text-slate-400">
                Abra um guia em rascunho, valide o conteúdo e use a ação de publicar quando estiver aprovado.
              </p>
            </section>
          )}
        </div>
        {showAnalytics ? (
          <div className="xl:col-span-5">
            <PainelAnalyticsPreview />
          </div>
        ) : null}
      </div>
    </>
  );
}

export function PainelDashboardHome() {
  const { session, role } = usePainelSession();

  if (!session || !role) return null;

  return (
    <div className="space-y-10">
      <PainelRoleWelcomeHeader role={role} name={session.name} />
      <RoleDashboardBody role={role} />
    </div>
  );
}
